import { Injectable } from "@angular/core";
import { HttpParams, HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";

interface IHttpResponse {
  error_code: number;
  error_msg: string;
  data: any;
}

interface IGetCleanerList {
  page?: number;
  page_size?: number;
  filter_unactive?: number;
  include_delete?: number;
}

interface IGetCleaningTaskList {
  expected_begin?: string;
  expected_end?: string;
  start_time_begin?: string;
  start_time_end?: string;
  end_time_begin?: string;
  end_time_end?: string;
  cleaner_id?: number;
  house_ids?: Array<number>;
  level?: number;
  currency_type: string;
  page?: number;
  page_size?: number;
}

interface ISetCleaningTask {
  house_id: number;
  cleaner_id: number;
  expected_date: string;
  expected_time: string;
  level: number;
  currency_type: string;
  fee: number;
  note?: string;
  gen_transaction: number;
  payment_method: number;
}

interface IUpdateHouse {
  house_id: number;
  status: number;
}

interface IAddCleaner {
  name: string;
  mobile: string;
  note?: string;
  house_ids?: Array<number>;
  email?: string;
}

interface IGetCleanerInfo {
  id: number;
}

interface IGetCleanFee {
  house_ids: any;
}

interface ISetCleanFee {
  house_ids: any;
  standard_fee?: number;
  simple_fee?: number;
  advance_fee?: number;
  currency_type: number;
  default_cleaner_id?: number;
}

interface IUpdateCleaningTask {
  id: number;
  expected_date: string;
  expected_time: string;
  level: number;
  currency_type: string;
  fee: number;
  note?: string;
  status: number;
}

interface IDeleteCleaner {
  id: number;
}

interface ICancelCleaningTask {
  id: number;
}

interface IUpdateCleanerStatus {
  id: number;
}

interface IUpdateCleaner {
  id: number;
  name: string;
  mobile: string;
  email?: string;
  note?: string;
  house_ids?: Array<number>;
}

interface IResponsibleHouseList {
  cleaner_id?: number;
}

interface ISendCleaningTask {
  task_ids: Array<number>;
}

interface IHouseList {
  begin: string;
  end: string;
  house_ids?: Array<number> | string;
  house_cleaning_status?: number;
  page?: number;
  page_size?: number;
}

interface IGetCleaningTaskCalendar {
  begin: string;
  end: string;
  house_ids?: Array<number>;
  page?: number;
  page_size: number;
}

interface IGetWechatQrcode {
  cleaner_id: number;
}

interface IGetHouseInfo {
  house_id: number;
}

interface IGetHouseGroupList {
  format?: string;
}

export enum HouseCleaningStatus {
  NoClean = 0,
  Cleaned = 1,
  Cleaning = 2,
  Dirty = 3,
}

export const HouseCleaningStatusInfo = {
  [HouseCleaningStatus.NoClean]: { name: "免打扫" },
  [HouseCleaningStatus.Cleaned]: { name: "已保洁" },
  [HouseCleaningStatus.Cleaning]: { name: "保洁中" },
  [HouseCleaningStatus.Dirty]: { name: "脏房" },
};

/** 保洁房间 */
const HOUSE_OPTION_API = "/api/reservation/house_group_list";
const GET_HOUSE_LIST_API = "/api/clean/house_list";
const UPDATE_CLEANING_HOUSE_API = "/api/clean/house_update";
const GET_CLEAN_FEE_API = "/api/clean/get_clean_fee";
const SET_CLEAN_FEE_APT = "/api/clean/set_clean_fee";
const GET_HOUSE_INFO_API = "/api/clean/house";
const SET_FEE_CURRENCY_LIST_API =
  "/api/clean/set_cleaning_task_currency_type_list";

/** 保洁员 */
const GET_CLEANER_LIST_API = "/api/clean/cleaner_list";
const DELETE_CLEANER_API = "/api/clean/cleaner_delete";
const UPDATE_CLEANER_STATUS_API = "/api/clean/cleaner_update_status";
const UPDATE_CLEANER_API = "/api/clean/cleaner_update";
const ADD_CLEANER_API = "/api/clean/cleaner_add";
const GET_CLEANER_INFO_API = "/api/clean/cleaner_get";
const RESPONSIBLE_HOUSE_LIST_API = "/api/clean/responsible_house_list";
const GET_WECHAT_QRCODE_API = "/api/clean/get_wechat_qrcode";

/** 保洁任务 */
const GET_CLEANING_TASK_LIST_API = "/api/clean/cleaning_task_list";
const SET_CLEANING_TASK_API = "/api/clean/set_cleaning_task";
const GET_CLEANING_TASK_CURRENCY_TYPE_LIST_API =
  "/api/clean/cleaning_task_currency_type_list";
const UPDATE_CLEANING_TASK_API = "/api/clean/cleaning_task_update";
const SEND_CLEANING_TASK_API = "/api/clean/cleaning_task_send";
const CANCEL_CLEANING_TASK_API = "/api/clean/cleaning_task_cancel";

/** 保洁日程 */
const GET_CLEANING_TASK_CALENDAR_API = "/api/clean/cleaning_task_calendar";

@Injectable({
  providedIn: 'root'
})
export class TaskCleanService {
  notNotifyNoCleaningAgain = false;
  constructor(private http: HttpClient) {}

  /** 保洁房间筛选列表 */
  getCleanHouseList(params: IGetHouseGroupList) {
    const query = new HttpParams().append("format", `${params.format}`);
    return this.http.get(HOUSE_OPTION_API, { params: query }) as Observable<
      IHttpResponse
    >;
  }

  /** 保洁员列表 */
  getCleanerList(params: IGetCleanerList) {
    const query = new HttpParams()
      .append("page", `${params.page}`)
      .append("page_size", `${params.page_size}`)
      .append("filter_unactive", `${params.filter_unactive}`)
      .append("include_delete", `${params.include_delete}`);
    return this.http.get(GET_CLEANER_LIST_API, { params: query }) as Observable<
      IHttpResponse
    >;
  }

  /** 保洁任务记录列表 */
  getCleaningTaskList(params: IGetCleaningTaskList) {
    const query = new HttpParams()
      .append("expected_begin", `${params.expected_begin}`)
      .append("expected_end", `${params.expected_end}`)
      .append("start_time_begin", `${params.start_time_begin}`)
      .append("start_time_end", `${params.start_time_end}`)
      .append("end_time_begin", `${params.end_time_begin}`)
      .append("end_time_end", `${params.end_time_end}`)
      .append("cleaner_id", `${params.cleaner_id}`)
      .append("house_ids", `${params.house_ids}`)
      .append("level", `${params.level}`)
      .append("currency_type", `${params.currency_type}`)
      .append("page", `${params.page}`)
      .append("page_size", `${params.page_size}`);
    return this.http.get(GET_CLEANING_TASK_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 安排保洁 */
  setCleaningTask(params: ISetCleaningTask) {
    return this.http.post(SET_CLEANING_TASK_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 更新保洁房间 */
  updateCleaningHouse(params: IUpdateHouse) {
    return this.http.post(UPDATE_CLEANING_HOUSE_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 添加保洁员 */
  addCleaner(params: IAddCleaner) {
    return this.http.post(ADD_CLEANER_API, params) as Observable<IHttpResponse>;
  }

  /** 获取保洁员信息 */
  getCleanerInfo(params: IGetCleanerInfo) {
    const query = new HttpParams().append("id", `${params.id}`);
    return this.http.get(GET_CLEANER_INFO_API, { params: query }) as Observable<
      IHttpResponse
    >;
  }

  /** 获取保洁费用 */
  getCleanFee(params: IGetCleanFee) {
    const query = new HttpParams().append("house_ids", `${params.house_ids}`);
    return this.http.get(GET_CLEAN_FEE_API, { params: query }) as Observable<
      IHttpResponse
    >;
  }

  /** 设置保洁费用 */
  setCleanFee(params: ISetCleanFee) {
    return this.http.post(SET_CLEAN_FEE_APT, params) as Observable<
      IHttpResponse
    >;
  }

  /** 修改保洁任务 */
  updateCleaningTask(params: IUpdateCleaningTask) {
    return this.http.post(UPDATE_CLEANING_TASK_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 删除保洁员 */
  deleteCleaner(params: IDeleteCleaner) {
    return this.http.post(DELETE_CLEANER_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 取消保洁任务 */
  cancelCleaningTask(params: ICancelCleaningTask) {
    return this.http.post(CANCEL_CLEANING_TASK_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 启用/禁用保洁员 */
  updateCleanerStatus(params: IUpdateCleanerStatus) {
    return this.http.post(UPDATE_CLEANER_STATUS_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 更新保洁员 */
  updateCleaner(params: IUpdateCleaner) {
    return this.http.post(UPDATE_CLEANER_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 负责的房间列表 */
  getResponsibleHouseList(params: IResponsibleHouseList) {
    const query = new HttpParams().append("cleaner_id", `${params.cleaner_id}`);
    return this.http.get(RESPONSIBLE_HOUSE_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 保洁任务记录货币列表 */
  getCurrencyList() {
    const query = new HttpParams();
    return this.http.get(GET_CLEANING_TASK_CURRENCY_TYPE_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 发送保洁任务 */
  sendCleaningTask(params: ISendCleaningTask) {
    return this.http.post(SEND_CLEANING_TASK_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 房间列表 */
  getHouseList(params: IHouseList) {
    const query = new HttpParams()
      .append("begin", `${params.begin}`)
      .append("end", `${params.end}`)
      .append("house_ids", `${params.house_ids}`)
      .append("house_cleaning_status", `${params.house_cleaning_status}`)
      .append("page", `${params.page}`)
      .append("page_size", `${params.page_size}`);
    return this.http.get(GET_HOUSE_LIST_API, { params: query }) as Observable<
      IHttpResponse
    >;
  }

  /** 获取保洁日程 */
  getCleaningTaskCalendar(params: IGetCleaningTaskCalendar) {
    const query = new HttpParams()
      .append("begin", `${params.begin}`)
      .append("end", `${params.end}`)
      .append("house_ids", `${params.house_ids}`)
      .append("page", `${params.page}`)
      .append("page_size", `${params.page_size}`);
    return this.http.get(GET_CLEANING_TASK_CALENDAR_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 获取保洁微信二维码 */
  getCleanerWechatQrcode(params: IGetWechatQrcode) {
    const query = new HttpParams().append("cleaner_id", `${params.cleaner_id}`);
    return this.http.get(GET_WECHAT_QRCODE_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 获取房间信息，尤其是默认保洁员信息 */
  getCleanHouseInfo(params: IGetHouseInfo) {
    const query = new HttpParams().append("house_id", `${params.house_id}`);
    return this.http.get(GET_HOUSE_INFO_API, { params: query }) as Observable<
      IHttpResponse
    >;
  }

  /** 房间设置费用币种选择列表 */
  getCleaningFeeCurrencyList() {
    const query = new HttpParams();
    return this.http.get(SET_FEE_CURRENCY_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }
}
